// ============================================================================
// DHAMMA PUBLISHING HOUSE — CENTRAL CONTENT CONFIG
// ----------------------------------------------------------------------------
// This is the single source of truth for editable copy, imagery, links, SEO,
// and the staged book-reveal system. Update values here to change the live
// site — no component or layout changes required.
// ============================================================================

export const brand = {
  name: "Dhamma Publishing House",
  legalName: "Dhamma Publishing House LLC",
  tagline: "Stories That Leave Something Behind.",
  philosophy: "Stories you don’t simply read. Stories you experience.",
  contactEmail: "hello@dhammapublishinghouse.com",
  copyright: "© 2026 Dhamma Publishing House LLC. All rights reserved.",
  logo: "/assets/dhamma-logo-official-transparent.png",
  logoMark: "/assets/dhamma-logo-official-transparent.png",
  crest: "/assets/dhamma-logo-official-transparent.png",
};

export const social = {
  instagram: "https://www.instagram.com/dhammapublishinghouse?utm_source=qr",
  tiktok: "https://www.tiktok.com/@dhammapublishinghouse?_r=1&_t=ZP-98za2OksdSA",
  facebook: "https://www.facebook.com/DhammaPublishingHouse?mibextid=wwXIfr",
};

export const nav = [
  { label: "Home", to: "/" },
  { label: "Books", to: "/books" },
  { label: "About Dhamma", to: "/about-dhamma" },
  { label: "About Bugg", to: "/about-bugg" },
  { label: "Contact", to: "/contact" },
];

export const images = {
  heroAtmosphere: "/assets/hero-atmosphere.jpg",
  heroBayGlass: "/assets/hero-bay-glass.jpg",
  teaserBayNoir: "/assets/teaser-bay-noir.jpg",
  experienceTexture: "/assets/experience-texture.jpg",
  experienceWarm: "/assets/experience-warm.jpg",
  buggPortrait: "/assets/bugg-portrait.png",
  keyMotif:
    "https://images.unsplash.com/photo-1680832033936-c1c0709e6f6b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  pagesMotif:
    "https://images.pexels.com/photos/7034134/pexels-photo-7034134.jpeg?auto=compress&cs=tinysrgb&w=1600",
};

// The shared, spoiler-safe teaser used on Home + Books.
export const teaser = {
  lines: [
    "Ten years ago, a little girl lost her father.",
    "She was told enough to survive the loss.",
    "Not enough to understand it.",
  ],
  seal: "Some secrets don’t stay buried.",
};

// ============================================================================
// STAGED BOOK REVEAL — scheduled + gated
// ----------------------------------------------------------------------------
// The campaign advances through 5 stages. The active stage is decided by
// getBookStage() below, using (in priority order):
//   1. manualStage  — force a stage now (overrides everything). null = off.
//   2. scheduleEnabled + schedule dates — auto-advance on/after each date.
//   3. otherwise stays at stage 1 (mystery teaser).
//
// DOUBLE SAFETY: even after a stage's date passes, a piece of content only
// appears once its field below is filled in. So nothing (title, cover,
// preorder, retailers) can leak before it is both scheduled AND provided.
//
//   Stage 1 = teasing (something is coming)   -> title hidden  [CURRENT]
//   Stage 2 = title reveal        (scheduled 2026-09-15)
//   Stage 3 = cover reveal        (scheduled 2026-09-29)
//   Stage 4 = preorder live       (scheduled 2026-10-06, retailer buttons)
//   Stage 5 = available now       (set a date when ready)
// ============================================================================
export const firstBook = {
  // Force a specific stage (1-5) right now, ignoring the schedule. null = use schedule.
  manualStage: 3, // Official title + cover reveal is live; preorder/retailers stay off

  // Master switch. true = follow the scheduled dates below. false = hold at stage 1.
  scheduleEnabled: true,

  // Scheduled activation datetimes (ISO, viewer's local time). null = not scheduled.
  schedule: {
    titleReveal: "2026-09-15T00:00:00", // Stage 2
    coverReveal: "2026-09-29T00:00:00", // Stage 3
    preorderLive: "2026-10-06T00:00:00", // Stage 4
    availableNow: null, // Stage 5 — set a date when the book releases
  },

  // Content — prepare anytime. Each item shows only when its stage is active
  // AND the field is filled. Leaving a field blank keeps it hidden.
  title: "Lost Girl: The Beginning",
  seriesName: "Book One of The Karter Legacy",
  author: "Bugg",
  coverImage: "/assets/lost-girl-cover.png",
  coverAlt: "Cover of Lost Girl: The Beginning by Bugg, Book One of The Karter Legacy.",
  description: "",
  releaseDate: "January 19, 2027",
  presaleDate: "December 1, 2026",
  presaleDateShort: "December 1",
  // Fixed presale moment — Dec 1, 2026, 12:00 AM Pacific (PST). Drives the Books countdown.
  presaleOpenAt: "2026-12-01T00:00:00-08:00",
  // Fixed release moment — Jan 19, 2027, 12:00 AM Pacific (PST). Both countdowns
  // retarget here automatically once presale opens.
  releaseOpenAt: "2027-01-19T00:00:00-08:00",
  formats: [], // e.g. ["Hardcover", "eBook", "Audiobook"]
  // Editable retailer buttons (shown from the preorder stage onward):
  // e.g. [{ name: "Bookshop.org", url: "https://bookshop.org/..." }]
  retailers: [],
  preorderUrl: "", // primary preorder button link
  reviews: [], // e.g. [{ quote: "...", source: "..." }]
  foundingReader: "", // Founding Reader program info
};

// Resolve the active campaign stage from the config above.
export function getBookStage() {
  const b = firstBook;
  if (typeof b.manualStage === "number") return b.manualStage;
  if (!b.scheduleEnabled) return 1;
  const now = Date.now();
  const s = b.schedule || {};
  const passed = (d) => d && now >= new Date(d).getTime();
  let stage = 1;
  if (passed(s.titleReveal)) stage = 2;
  if (passed(s.coverReveal)) stage = 3;
  if (passed(s.preorderLive)) stage = 4;
  if (passed(s.availableNow)) stage = 5;
  return stage;
}

export const home = {
  hero: {
    headline: "Stories That Leave Something Behind.",
    supporting: "Stories you don’t simply read. Stories you experience.",
    primaryCta: { label: "Enter Dhamma", to: "/about-dhamma" },
    secondaryCta: { label: "Join the Community", to: "/#community" },
  },
  whatDhamma: {
    headline: "Stories Like Ours Deserve to Be Told.",
    body:
      "Dhamma Publishing House is an independent home for multidimensional stories, unforgettable characters, complicated truths, and the kind of books that stay with you long after the final page.",
    pullQuote: "We’re not interested in stories that leave you feeling nothing.",
  },
  storyTeaser: {
    overline: "The Debut Novel",
    headline: "Our First Story Is Coming.",
    cta: { label: "Discover Lost Girl", to: "/books" },
  },
  storyteller: {
    overline: "Meet the Storyteller",
    name: "Meet Bugg",
    subheading: "Author. Storyteller. Founder.",
    body:
      "Bugg is a lifelong storyteller and the founder of Dhamma Publishing House. Dhamma was created as a home for the kinds of stories she wanted to tell — stories filled with real people, real conversations, culture, love, mess, mystery, emotion, and truth.",
    cta: { label: "Meet Bugg", to: "/about-bugg" },
  },
  experience: {
    words: ["Mystery.", "Emotion.", "Danger.", "Love.", "Legacy.", "Truth."],
    closing: ["A Dhamma book should never simply be consumed.", "It should be experienced."],
  },
  community: {
    headline: "Come Inside.",
    body:
      "Get first access to reveals, releases, behind-the-scenes moments, reader opportunities, events, and whatever Dhamma is building next.",
    cta: "Join the Dhamma Community",
    footnote: "You know us… but you don’t know us yet.",
  },
};

export const aboutDhamma = {
  headline: "Stories Like Ours Deserve to Be Told.",
  opening: "Dhamma was built on a simple belief: stories like ours deserve to be told.",
  body: [
    "Dhamma Publishing House is committed to multidimensional, multicultural, emotionally immersive storytelling — books rooted in representation, individuality, and emotional honesty.",
    "We publish with cultural specificity and a devotion to community. We write toward unforgettable characters and the difficult truths most stories avoid, threading mystery through the meaningful.",
  ],
  statements: [
    "Dhamma publishes stories you experience, not simply stories you read.",
    "Dhamma is an acquired taste.",
  ],
  acquiredTaste:
    "Dhamma does not believe every story needs to be for every reader. The goal is not to flatten stories into something universally palatable. The goal is to tell stories honestly — and find the readers they were meant for.",
};

export const aboutBugg = {
  name: "Bugg",
  subheading: "Author • Storyteller • Founder",
  // First-person body, structured into labeled editorial sections. A section
  // either holds paragraphs (with a small gold label) or the pull quote.
  // Copy is exact — do not edit wording.
  sections: [
    {
      label: "Who I Am",
      paragraphs: [
        "I’m MeLanie, but some people know me as Bugg.",
        "“Bugg” comes from LadyyBugg, a nickname my mother gave me, and it’s the name I chose to write under because it feels the most like me.",
        "I’m from the Bay Area, California, with family roots in Oakland. Storytelling has always been part of my life through music, poetry, short stories, imagination, and now, novels.",
      ],
    },
    {
      label: "Why I Write",
      paragraphs: [
        "I started writing my first novel more than a decade ago during one of the hardest periods of my life. I was navigating personal, emotional, financial, and relationship struggles, and writing became somewhere I could put everything I didn’t know how to say out loud.",
        "What started as an outlet became something much bigger.",
        "Life interrupted the manuscript more than once, but I kept coming back to it. Years later, after praying for clarity about my purpose, I realized the story had never really left me.",
      ],
    },
    {
      pullQuote: "“It had been waiting for me to come back to it.”",
    },
    {
      label: "Why Dhamma Exists",
      paragraphs: [
        "The stories I want to tell are the kinds of stories I connected with as a younger reader. Stories with recognizable people, honest conversations, culture, mystery, emotion, edge, and characters who feel like somebody you might actually know.",
        "That’s why I created Dhamma Publishing House.",
        "I wanted the stories in my head to always have a home.",
      ],
    },
  ],
  closing: "I created Dhamma so the stories in my head would always have a home.",
  closingSupport: "And now, I get to share them with you.",
};

export const contact = {
  heading: "Let’s Connect.",
  inquiryTypes: [
    "General Inquiry",
    "Media / Press",
    "Booksellers & Librarians",
    "Events",
    "Reader Inquiry",
  ],
  note: "Response times may vary. Every message reaches us — we read each one.",
};


// ARC applications open at a fixed moment — Sept 19, 2026, 10:00 AM Pacific (PDT).
export const arcOpenAt = "2026-09-19T10:00:00-07:00";

export const books = {
  headline: "Something Is Coming.",
  supporting: "Our first story has been waiting a long time to be told.",
  cta: { label: "Be First to Know", to: "/#community" },
};

export const seo = {
  "/": {
    title: "Lost Girl: The Beginning by Bugg | Dhamma Publishing House",
    description:
      "Lost Girl: The Beginning — Book One of The Karter Legacy by Bugg. Coming January 19, 2027 from Dhamma Publishing House. Presale begins December 1, 2026. Stories that leave something behind.",
    image: "/assets/lost-girl-cover.png",
  },
  "/books": {
    title: "Lost Girl: The Beginning — Book One of The Karter Legacy | Dhamma Publishing House",
    description:
      "Lost Girl: The Beginning by Bugg, Book One of The Karter Legacy. Coming January 19, 2027 from Dhamma Publishing House. Presale begins December 1, 2026.",
    image: "/assets/lost-girl-cover.png",
  },
  "/about-dhamma": {
    title: "About Dhamma | Stories You Experience",
    description:
      "Dhamma publishes stories you experience, not simply stories you read. An independent home for multidimensional, culturally specific, emotionally honest storytelling.",
  },
  "/about-bugg": {
    title: "About Bugg | Author, Storyteller, Founder",
    description:
      "Meet Bugg — author, storyteller, and founder of Dhamma Publishing House. From the Bay Area, with family roots in Oakland.",
  },
  "/contact": {
    title: "Contact | Dhamma Publishing House",
    description:
      "Connect with Dhamma Publishing House — general inquiries, media and press, booksellers and librarians, events, and readers.",
  },
  "/privacy": {
    title: "Privacy | Dhamma Publishing House",
    description: "How Dhamma Publishing House handles the information you share with us.",
  },
  "/arc": {
    title: "Join the Dhamma ARC Team | Dhamma Publishing House",
    description:
      "Apply to become an Advance Reader for Dhamma Publishing House. Read early, review honestly, and help us leave something behind.",
  },
};
